import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  TableRestaurant as TableIcon,
  Category as CategoryIcon,
  Message as MessageIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { shopAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const ShopsPage = () => {
  const { isAdmin, agent } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    id: '',
    address: '',
    description: 'Quality You Can Trust',
    phone: '',
    email: '',
    your_name: '',
    plan: 'no_plan',
    trial_mode: false,
    mobileVerified: false,
    enable_reference_id: false,
  });
  const [filters, setFilters] = useState({
    plan: 'all',
    trial_mode: 'all',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const usernameManuallyEdited = useRef(false);
  const [whatsappMenuAnchor, setWhatsappMenuAnchor] = useState(null);
  const [selectedShopForWhatsApp, setSelectedShopForWhatsApp] = useState(null);

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [shops, filters]);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const response = await shopAPI.getShops();
      setShops(response.data || []);
    } catch (error) {
      console.error('Error fetching shops:', error);
      setError('Failed to fetch shops');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...shops];

    if (filters.plan !== 'all') {
      filtered = filtered.filter((shop) => shop.plan === filters.plan);
    }

    if (filters.trial_mode !== 'all') {
      const trialModeValue = filters.trial_mode === 'true';
      filtered = filtered.filter((shop) => shop.trial_mode === trialModeValue);
    }

    setFilteredShops(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleOpenDialog = (shop = null) => {
    usernameManuallyEdited.current = false;
    if (shop) {
      setEditingShop(shop);
      const phoneValue = shop.phone || shop.storeTelephone || shop.mobile || '';
      setFormData({
        name: shop.name || '',
        id: shop.id || '',
        address: shop.address || '',
        description: shop.description || 'Quality You Can Trust',
        phone: phoneValue ? String(phoneValue) : '',
        email: shop.email || '',
        your_name: '',
        plan: shop.plan || 'no_plan',
        trial_mode: shop.trial_mode || false,
        mobileVerified: shop.mobileVerified || false,
        enable_reference_id: shop.enable_reference_id || false,
      });
    } else {
      setEditingShop(null);
      setFormData({
        name: '',
        id: '',
        address: '',
        description: 'Quality You Can Trust',
        phone: '',
        email: '',
        your_name: '',
        plan: 'no_plan',
        trial_mode: false,
        mobileVerified: false,
        enable_reference_id: false,
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingShop(null);
    usernameManuallyEdited.current = false;
    setFormData({
      name: '',
      id: '',
      address: '',
      description: 'Quality You Can Trust',
      phone: '',
      email: '',
      your_name: '',
      plan: 'no_plan',
      trial_mode: false,
      mobileVerified: false,
      enable_reference_id: false,
    });
    setError('');
    setSuccess('');
  };

  // Auto-generate shop username from shop name
  useEffect(() => {
    if (formData.name && formData.name.trim() && !usernameManuallyEdited.current && !editingShop) {
      const formattedUsername = formData.name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 63);

      if (formattedUsername && formattedUsername.length > 0) {
        setFormData((prev) => ({
          ...prev,
          id: formattedUsername,
        }));
      }
    }
  }, [formData.name, editingShop]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'id') {
      usernameManuallyEdited.current = true;
    }
    
    if (name === 'id') {
      // Normalize shop username: lowercase, trim
      const normalizedValue = value.toLowerCase().trim();
      setFormData({
        ...formData,
        [name]: normalizedValue,
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError('Shop name is required');
      return;
    }
    if (formData.id && !formData.id.trim()) {
      setError('Shop username is required if provided');
      return;
    }

    // Validate shop username format if provided
    if (formData.id && formData.id.trim()) {
      const usernamePattern = /^(?!-)(?!.*-$)(?![0-9]+$)[a-z0-9-]{1,63}$/;
      if (!usernamePattern.test(formData.id.trim())) {
        setError(
          'Shop username can contain letters (a–z), numbers (0–9), and hyphens. It cannot start or end with a hyphen, cannot be only numbers, and must be at most 63 characters.'
        );
        return;
      }
    }

    // Validate email if provided
    if (formData.email && formData.email.trim()) {
      const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailPattern.test(formData.email.trim())) {
        setError('Please enter a valid email address');
        return;
      }
    }

    try {
      setError('');
      setSuccess('');
      setSubmitting(true);

      const payload = {
        name: formData.name.trim(),
        address: formData.address?.trim(),
        description: formData.description?.trim(),
        plan: formData.plan,
        trial_mode: formData.trial_mode,
        mobileVerified: formData.mobileVerified,
        enable_reference_id: formData.enable_reference_id,
      };

      // Only include optional fields if they have values
      if (formData.id && formData.id.trim()) {
        payload.id = formData.id.trim();
      }
      if (formData.email && formData.email.trim()) {
        payload.email = formData.email.trim();
      }
      if (formData.phone) {
        const phoneValue = String(formData.phone).trim();
        if (phoneValue) {
          payload.phone = phoneValue;
        }
      }

      if (editingShop) {
        await shopAPI.updateShop(editingShop._id, payload);
        setSuccess('Shop updated successfully');
      } else {
        await shopAPI.createShop(payload);
        setSuccess('Shop created successfully');
      }

      setTimeout(() => {
        handleCloseDialog();
        fetchShops();
      }, 1000);
    } catch (error) {
      console.error('Error saving shop:', error);
      setError(
        error.response?.data?.message || 'Failed to save shop. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const buildShopUrl = (username) => {
    if (!username || !username.trim()) return '';
    return `https://${username.trim()}.menunix.com`;
  };

  // WhatsApp message templates with emojis
  const whatsappTemplates = {
    'demo only + invalid fssai': `Hi 🙂 Welcome to Menunix!

🔗 View your menu (customers see this):

https://{{username}}.menunix.com

✏️ Edit / update your menu (dashboard):

https://app.menunix.com

⚠️ We noticed the FSSAI number needs correction before sharing the menu with customers.

Please update the correct FSSAI number in the dashboard,

or just send it here and we'll fix it for you.

You can also send a photo of your menu — we'll update everything together 😊`,
    'demo only': `Hi 🙂 Welcome to Menunix!

🔗 View your menu (customers see this):

https://{{username}}.menunix.com

✏️ Edit / update your menu (dashboard):

https://app.menunix.com`,
    'invalid fssai': `Hi 🙂 Welcome to Menunix!

🔗 View your menu (customers see this):

https://{{username}}.menunix.com

✏️ Edit / update your menu (dashboard):

https://app.menunix.com

⚠️ We noticed the FSSAI number needs correction before sharing the menu with customers.

Please update the correct FSSAI number in the dashboard,

or just send it here and we'll fix it for you.`,
    'only help': `Hi 🙂 I'm from Menunix. Just checking — need any help with menu setup?`,
    'no fssai': `Hi 🙂 Quick update from Menunix.

To keep your menu compliant, we need your FSSAI number.

👉 You can update it in the dashboard

or simply reply with your FSSAI number here, and we'll add it for you.`,
    'Fssai thankyou': `Thanks 🙂 We've received your FSSAI number and updated it in Menunix.`,
    'menu thankyou': `Thanks 🙂 We've received your menu photo.

We'll update your menu in Menunix and notify you once it's live.`,
    'menu is live + qr code': `✅ Your menu is now live in Menunix!

🔗 View your menu: {{MENU_LINK}}

📱 QR code ready — you can share or print it for your customers:

{{QR_CODE_IMAGE}}

✏️ Edit or update your menu anytime: {{DASHBOARD_LINK}}`,
    'Day 7 – Reminder': `🔹 Day 7 – Reminder

Hi 🙂

Your Menunix free trial is active.

You can receive QR table orders and customer orders.

💳 Pay Now (manual): {{PAY_NOW_LINK}}

🔁 Auto-Pay (recommended): {{AUTOPAY_LINK}}

ℹ️ For manual payment, please share the payment screenshot for activation.`,
    'Day 13 – Trial Ending Soon': `🔹 Day 13 – Trial Ending Soon

⏳ Your Menunix trial ends in 2 days.

To continue QR menu + table orders, please upgrade.

💳 Pay Now (manual): {{PAY_NOW_LINK}}

🔁 Auto-Pay (recommended): {{AUTOPAY_LINK}}

📩 For manual payment, please share the payment screenshot for activation.`,
    'Day 15 – Final Message': `🔹 Day 15 – Final Message

🚨 Trial ends today

Upgrade now to keep table ordering and customer orders active.

💳 Pay Now (manual): {{PAY_NOW_LINK}}

🔁 Auto-Pay (recommended): {{AUTOPAY_LINK}}

📸 After manual payment, share screenshot to activate.`,
  };

  const handleWhatsAppClick = (shop, event) => {
    if (!shop) return;
    
    const phoneNumber = shop.phone || shop.storeTelephone || shop.mobile;
    if (!phoneNumber) return;

    setSelectedShopForWhatsApp(shop);
    if (event && event.currentTarget) {
      setWhatsappMenuAnchor(event.currentTarget);
    } else {
      // If no event, open directly with default template
      sendWhatsAppMessage(phoneNumber, shop.id, 'demo only');
    }
  };

  const handleCloseWhatsAppMenu = () => {
    setWhatsappMenuAnchor(null);
    setSelectedShopForWhatsApp(null);
  };

  const sendWhatsAppMessage = (phoneNumber, shopUsername, templateName) => {
    if (!phoneNumber) return;
    
    // Convert to string in case it's a number
    const phoneStr = String(phoneNumber);
    
    // Remove all non-digit characters (including +)
    const cleanedPhone = phoneStr.replace(/[^\d]/g, '');
    
    if (!cleanedPhone) return;
    
    // Get template message
    const template = whatsappTemplates[templateName] || whatsappTemplates['demo only'];
    
    // Build URLs
    const menuLink = shopUsername ? `https://${shopUsername}.menunix.com` : 'https://your-shop.menunix.com';
    const dashboardLink = 'https://app.menunix.com';
    
    // Generate QR code image URL using QR code service
    const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(menuLink)}`;
    
    // Replace all placeholders
    let message = template
      .replace(/\{\{username\}\}/g, shopUsername || 'your-shop')
      .replace(/\{\{MENU_LINK\}\}/g, menuLink)
      .replace(/\{\{DASHBOARD_LINK\}\}/g, dashboardLink)
      .replace(/\{\{QR_CODE_IMAGE\}\}/g, qrCodeImageUrl);
    
    // Encode message for URL - encodeURIComponent properly handles emojis
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp using api.whatsapp.com format (same as working example)
    window.open(`https://api.whatsapp.com/send/?phone=${cleanedPhone}&text=${encodedMessage}`, '_blank');
    
    handleCloseWhatsAppMenu();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Typography variant={isMobile ? "h5" : "h4"}>Shops</Typography>
       <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={isMobile}
          size={isMobile ? "medium" : "large"}
        >
          Create Shop
        </Button> 
      </Box>

      {shops.length > 0 && (
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2} 
          mb={3}
        >
          <FormControl size="small" fullWidth={isMobile} sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={filters.plan}
              label="Plan"
              onChange={(e) => handleFilterChange('plan', e.target.value)}
            >
              <MenuItem value="all">All Plans</MenuItem>
              <MenuItem value="no_plan">No Plan</MenuItem>
              <MenuItem value="basic">Basic</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth={isMobile} sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Trial Mode</InputLabel>
            <Select
              value={filters.trial_mode}
              label="Trial Mode"
              onChange={(e) => handleFilterChange('trial_mode', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {shops.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <StoreIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" gutterBottom>
            No shops found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {isAdmin()
              ? 'No shops have been created yet.'
              : 'You haven\'t created any shops yet.'}
          </Typography>
          {/* <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ mt: 2 }}
            fullWidth={isMobile}
          >
            Create Your First Shop
          </Button> */}
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredShops.map((shop) => (
            <Card key={shop._id} sx={{ width: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" fontWeight="medium" sx={{ flex: 1 }}>
                    {shop.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(shop)}
                    color="primary"
                    title="Edit Shop"
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                
                {shop.id && (
                  <Box mb={1.5}>
                    <Box
                      component="a"
                      href={buildShopUrl(shop.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                        {buildShopUrl(shop.id)}
                      </Typography>
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                )}

                <Stack spacing={1.5}>
                  {shop.description && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Tagline
                      </Typography>
                      <Typography variant="body2">{shop.description}</Typography>
                    </Box>
                  )}

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {shop.phone || shop.storeTelephone || shop.mobile ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {shop.phone || shop.storeTelephone || shop.mobile}
                        </Typography>
                      </Box>
                    ) : null}

                    <Box display="flex" alignItems="center" gap={0.5}>
                      {shop.mobileVerified ? (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main" fontSize="0.75rem">
                            Verified
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          <Typography variant="body2" color="error.main" fontSize="0.75rem">
                            Not Verified
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Chip
                      label={shop.plan || 'no_plan'}
                      size="small"
                      color={
                        shop.plan === 'basic'
                          ? 'primary'
                          : shop.plan === 'pro'
                          ? 'secondary'
                          : shop.plan === 'no_plan'
                          ? 'default'
                          : 'info'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                    <Chip
                      label={shop.trial_mode ? 'Trial: Yes' : 'Trial: No'}
                      size="small"
                      color={shop.trial_mode ? 'warning' : 'default'}
                    />
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Created By
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {shop.createdBy?.name || shop.CreatedAgent?.name || 'Unknown'}
                    </Typography>
                    {(shop.createdBy?.email || shop.CreatedAgent?.email) && (
                      <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                        {shop.createdBy?.email || shop.CreatedAgent?.email}
                      </Typography>
                    )}
                  </Box>

                  {isAdmin() && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Updated By
                      </Typography>
                      <Typography variant="body2" fontSize="0.875rem">
                        {shop.updatedBy?.name || shop.UpdatedAgent?.name || 'Unknown'}
                      </Typography>
                      {(shop.updatedBy?.email || shop.UpdatedAgent?.email) && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                          {shop.updatedBy?.email || shop.UpdatedAgent?.email}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Created At
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {formatDate(shop.createdAt)}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1} flexWrap="wrap" pt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<InventoryIcon />}
                      onClick={() => navigate(`/shops/${shop._id}/products`)}
                      sx={{ flex: { xs: '1 1 auto', sm: 'none' }, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' } }}
                    >
                      Products
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CategoryIcon />}
                      onClick={() => navigate(`/shops/${shop._id}/categories`)}
                      sx={{ flex: { xs: '1 1 auto', sm: 'none' }, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' } }}
                    >
                      Categories
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TableIcon />}
                      onClick={() => navigate(`/shops/${shop._id}/tables`)}
                      sx={{ flex: { xs: '1 1 auto', sm: 'none' }, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' } }}
                    >
                      Tables
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PeopleIcon />}
                      onClick={() => navigate(`/shops/${shop._id}/shop-owners`)}
                      sx={{ flex: { xs: '1 1 auto', sm: 'none' }, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' } }}
                    >
                      Shop Owners
                    </Button>
                    {(shop.phone || shop.storeTelephone || shop.mobile) && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<MessageIcon />}
                        onClick={(e) => handleWhatsAppClick(shop, e)}
                        sx={{ flex: { xs: '1 1 auto', sm: 'none' }, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' } }}
                      >
                        WhatsApp
                      </Button>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '8px', sm: '16px' },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Username</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Tagline</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Phone</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Mobile Verified</TableCell>
                <TableCell sx={{ minWidth: 80 }}>Plan</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Trial Mode</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Created By</TableCell>
                {isAdmin() && <TableCell sx={{ minWidth: 120 }}>Updated By</TableCell>}
                <TableCell sx={{ minWidth: 140 }}>Created At</TableCell>
                <TableCell align="right" sx={{ minWidth: 180 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShops.map((shop) => (
                <TableRow key={shop._id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {shop.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {shop.id ? (
                      <Box
                        component="a"
                        href={buildShopUrl(shop.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontWeight: 500 }}
                        >
                          {buildShopUrl(shop.id)}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {shop.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{shop.phone || shop.storeTelephone || shop.mobile || '-'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {shop.mobileVerified ? (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main">
                            Verified
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                          <Typography variant="body2" color="error.main">
                            Not Verified
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={shop.plan || 'no_plan'}
                      size="small"
                      color={
                        shop.plan === 'basic'
                          ? 'primary'
                          : shop.plan === 'pro'
                          ? 'secondary'
                          : shop.plan === 'no_plan'
                          ? 'default'
                          : 'info'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={shop.trial_mode ? 'Yes' : 'No'}
                      size="small"
                      color={shop.trial_mode ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {shop.createdBy?.name || shop.CreatedAgent?.name || 'Unknown'}
                    {(shop.createdBy?.email || shop.CreatedAgent?.email) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {shop.createdBy?.email || shop.CreatedAgent?.email}
                      </Typography>
                    )}
                  </TableCell>
                  {isAdmin() && (
                    <TableCell>
                      {shop.updatedBy?.name || shop.UpdatedAgent?.name || 'Unknown'}
                      {(shop.updatedBy?.email || shop.UpdatedAgent?.email) && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {shop.updatedBy?.email || shop.UpdatedAgent?.email}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(shop.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/shops/${shop._id}/products`)}
                        color="primary"
                        title="View Products"
                      >
                        <InventoryIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/shops/${shop._id}/categories`)}
                        color="info"
                        title="View Categories"
                      >
                        <CategoryIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/shops/${shop._id}/tables`)}
                        color="secondary"
                        title="View Tables"
                      >
                        <TableIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/shops/${shop._id}/shop-owners`)}
                        color="warning"
                        title="View Shop Owners"
                      >
                        <PeopleIcon />
                      </IconButton>
                      {(shop.phone || shop.storeTelephone || shop.mobile) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleWhatsAppClick(shop, e)}
                          color="success"
                          title="Send WhatsApp Message"
                        >
                          <MessageIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(shop)}
                        color="primary"
                        title="Edit Shop"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>
          {editingShop ? 'Edit Shop' : 'Create New Shop'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Shop Name *"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
                error={!!error && !formData.name.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="id"
                label="Shop Username"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.id}
                onChange={handleInputChange}
                helperText={
                  formData.id
                    ? `Your store will be available at: ${buildShopUrl(formData.id)}`
                    : 'Used for your public store link. Auto-generated from shop name.'
                }
                error={!!error && formData.id && !formData.id.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="description"
                label="Tagline"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                inputProps={{ maxLength: 60 }}
                helperText={`${formData.description.length}/60 characters`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="phone"
                label="Phone Number"
                type="tel"
                fullWidth
                variant="outlined"
                value={formData.phone}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="email"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Optional. Used for payment notifications."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="address"
                label="Address"
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Plan</InputLabel>
                <Select
                  name="plan"
                  value={formData.plan}
                  label="Plan"
                  onChange={handleInputChange}
                >
                  <MenuItem value="no_plan">No Plan</MenuItem>
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.trial_mode}
                    onChange={handleInputChange}
                    name="trial_mode"
                  />
                }
                label="Trial Mode"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.mobileVerified}
                    onChange={handleInputChange}
                    name="mobileVerified"
                  />
                }
                label="Mobile Verified"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enable_reference_id}
                    onChange={handleInputChange}
                    name="enable_reference_id"
                  />
                }
                label="Enable Reference ID"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : editingShop ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Template Selection Menu */}
      <Menu
        anchorEl={whatsappMenuAnchor}
        open={Boolean(whatsappMenuAnchor)}
        onClose={handleCloseWhatsAppMenu}
        PaperProps={{
          sx: { minWidth: 250 }
        }}
      >
        <MenuItem disabled>
          <ListItemText 
            primary="Select Template" 
            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
          />
        </MenuItem>
        <Divider />
        {Object.keys(whatsappTemplates).map((templateName) => (
          <MenuItem
            key={templateName}
            onClick={() => {
              const phoneNumber = selectedShopForWhatsApp?.phone || 
                                  selectedShopForWhatsApp?.storeTelephone || 
                                  selectedShopForWhatsApp?.mobile;
              const shopUsername = selectedShopForWhatsApp?.id || '';
              sendWhatsAppMessage(phoneNumber, shopUsername, templateName);
            }}
          >
            <ListItemText primary={templateName} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ShopsPage;

